import React from 'react';
import { celebrationYear } from './modes';

// --- Accessories worn by the header logo -------------------------------------------------
const HATS = {
  santa: (
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <path d="M8 40 C 14 10, 40 2, 56 14 L 50 38 Z" fill="#dc2626" />
      <circle cx="56" cy="14" r="7" fill="#fff" />
      <rect x="4" y="36" width="50" height="12" rx="6" fill="#fff" />
    </svg>
  ),
  witch: (
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <path d="M30 2 L 48 40 L 12 40 Z" fill="#111827" />
      <ellipse cx="32" cy="44" rx="30" ry="7" fill="#111827" />
      <path d="M16 34 L 46 34 L 44 40 L 18 40 Z" fill="#7c3aed" />
      <rect x="29" y="33" width="6" height="7" fill="#fbbf24" />
    </svg>
  ),
  leprechaun: (
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <rect x="14" y="6" width="36" height="34" rx="4" fill="#15803d" />
      <ellipse cx="32" cy="44" rx="30" ry="7" fill="#166534" />
      <rect x="14" y="28" width="36" height="9" fill="#111827" />
      <rect x="27" y="26" width="10" height="13" rx="1" fill="#fbbf24" />
      <rect x="30" y="29" width="4" height="7" fill="#111827" />
    </svg>
  ),
  bunny: (
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <ellipse cx="22" cy="26" rx="9" ry="24" fill="#fff" stroke="#e5e7eb" />
      <ellipse cx="22" cy="28" rx="4.5" ry="17" fill="#fbcfe8" />
      <ellipse cx="42" cy="26" rx="9" ry="24" fill="#fff" stroke="#e5e7eb" transform="rotate(12 42 26)" />
      <ellipse cx="42" cy="28" rx="4.5" ry="17" fill="#fbcfe8" transform="rotate(12 42 26)" />
    </svg>
  ),
  party: (
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <path d="M32 2 L 54 48 L 10 48 Z" fill="#f472b6" />
      <path d="M32 2 L 42 23 L 22 23 Z" fill="#60a5fa" />
      <path d="M18 31 L 46 31 L 50 40 L 14 40 Z" fill="#fbbf24" />
      <circle cx="32" cy="3" r="4" fill="#34d399" />
    </svg>
  ),
};

export const Accessory = ({ accessory, size = 48 }) => {
  if (!accessory) return null;
  if (HATS[accessory]) {
    const hat = accessory === 'bunny' ? { top: -size * 0.16, left: size * 0.22, width: size * 0.55, rotate: 0 } : { top: -size * 0.2, left: -size * 0.16, width: size * 0.7, rotate: -20 };
    return (
      <span className="absolute pointer-events-none" style={{ top: hat.top, left: hat.left, width: hat.width, transform: `rotate(${hat.rotate}deg)`, filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.3))' }} data-testid={`szn-accessory-${accessory}`}>
        {HATS[accessory]}
      </span>
    );
  }
  return (
    <span className="szn-emoji absolute pointer-events-none szn-bob" style={{ top: -size * 0.08, right: -size * 0.22, fontSize: size * 0.42 }} data-testid="szn-accessory-emoji">
      {accessory}
    </span>
  );
};

// --- Corner stickers --------------------------------------------------------------------
// Bottom-left leaves room for the BackToTop button (desktop: left 32px, 44px wide).
const CORNER_POS = (headerOffset, isMobile) => ({
  tl: { top: headerOffset + 10, left: 10 },
  tr: { top: headerOffset + 10, right: 10 },
  bl: { bottom: 10, left: isMobile ? 10 : 84 },
  br: { bottom: 10, right: 10 },
});
const CORNER_SCALE = { subtle: 0.8, normal: 1, festive: 1.2 };

export const Corners = ({ corners, intensity, isMobile, headerOffset }) => {
  if (!corners) return null;
  const pos = CORNER_POS(headerOffset, isMobile);
  const scale = (CORNER_SCALE[intensity] || 1) * (isMobile ? 0.62 : 1);
  return Object.entries(corners).map(([key, items]) => {
    if (!items?.length) return null;
    const shown = intensity === 'subtle' ? items.slice(0, 1) : items;
    const isTop = key.startsWith('t');
    return (
      <div key={key} className={`absolute flex items-end gap-1 ${key.endsWith('r') ? 'flex-row-reverse' : ''}`} style={pos[key]} data-testid={`szn-corner-${key}`}>
        {shown.map((g, i) => (
          <span key={i} className={`szn-emoji ${isTop ? 'szn-swing' : 'szn-bob'}`} style={{ fontSize: (i === 0 ? 60 : 42) * scale, animationDelay: `${i * -1.1}s` }}>{g}</span>
        ))}
      </div>
    );
  });
};

// --- Bottom strip (snow drift / water ripple) --------------------------------------------
export const BottomStrip = ({ kind, isMobile }) => {
  if (!kind) return null;
  const h = isMobile ? 22 : 36;
  const wave = 'M0 20 C 150 0, 300 40, 450 20 S 750 0, 900 20 S 1200 40, 1200 20 L 1200 40 L 0 40 Z';
  const snow = kind === 'snow';
  return (
    <svg className="absolute left-0 right-0 bottom-0 w-full" height={h} viewBox="0 0 1200 40" preserveAspectRatio="none" aria-hidden="true" data-testid={`szn-bottom-${kind}`} style={{ filter: snow ? 'drop-shadow(0 -2px 3px rgba(0,0,0,0.08))' : undefined }}>
      <path d={wave} fill={snow ? '#ffffff' : '#5cc8d6'} opacity={snow ? 0.95 : 0.35} />
      <path d={wave} fill={snow ? '#eef6ff' : '#2bb5c6'} opacity={snow ? 0.8 : 0.35} transform="translate(80 8)" />
    </svg>
  );
};

// --- Sign (top-right under the header) ----------------------------------------------------
export const Sign = ({ sign, isMobile, headerOffset }) => {
  if (!sign) return null;
  const text = sign.text === 'year' ? String(celebrationYear()) : sign.text;
  if (sign.style === 'neon') {
    return (
      <div className="absolute" style={{ top: headerOffset + (isMobile ? 58 : 80), right: isMobile ? 10 : 28 }} data-testid="szn-sign-neon">
        <span className="szn-neon" style={{ '--neon': sign.color, fontSize: isMobile ? 20 : 34 }}>{text}</span>
      </div>
    );
  }
  return (
    <div className="szn-tag absolute" style={{ top: headerOffset, right: isMobile ? 10 : 36 }} data-testid="szn-sign-tag">
      <span className="szn-thread" style={{ height: isMobile ? 54 : 76, background: 'rgba(0,0,0,0.35)' }} />
      <span className="szn-tag-body" style={{ background: sign.bg, color: sign.color, fontSize: isMobile ? 10 : 13, padding: isMobile ? '5px 10px' : '8px 16px' }}>{text}</span>
    </div>
  );
};
